// context/GeoContext.tsx
import { useRouter } from 'next/router'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import {
  type GeoScenario,
  resolveGeoScenario
} from 'utils/geo/resolveGeoScenario'

import { useLocale } from './LocaleContext'

interface GeoContextType extends Omit<GeoScenario, 'currentSite'> {
  /** Home URL of the other site, for the cross-site CTA (from env) */
  crossSiteHomeUrl: string | null
}

const GeoContext = createContext<GeoContextType>({
  userCountry: null,
  currentLocale: null,
  visitorSite: null,
  isMismatch: false,
  isCrossSite: false,
  isUnsupportedCountry: false,
  suggestedLocale: null,
  shouldShowModal: false,
  crossSiteHomeUrl: null
})

export const GeoProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const router = useRouter()
  const [userCountry, setUserCountry] = useState<string | null>(null)

  useEffect(() => {
    // Perform a lightweight HEAD request to current route to read headers output by middleware
    fetch(window.location.href, { method: 'HEAD' })
      .then((res) => {
        const country = res.headers.get('x-user-country')
        if (country) {
          setUserCountry(country.toLowerCase())
        }
      })
      .catch((err) => console.error('Error fetching geo context:', err))
  }, [router.asPath])

  const { locale: pathLocale, brand } = useLocale()

  // Memoized: resolver runs only when the inputs change, not on every
  // provider re-render (route changes, pageProps updates, etc.)
  const scenario = useMemo(
    () => resolveGeoScenario(userCountry, pathLocale, brand),
    [userCountry, pathLocale, brand]
  )

  useEffect(() => {
    // Unsupported country: we cannot block these users (QA/UX decision).
    // Log only for now — country-picker modal comes in a separate feature.
    if (scenario.isUnsupportedCountry) {
      // eslint-disable-next-line no-console
      console.log('[GeoProvider] visitor from unsupported country', {
        userCountry: scenario.userCountry
      })
    }
  }, [scenario])

  // Memoized: a new value object on every render would re-render all
  // consumers of this context even when the scenario didn't change
  const contextValue = useMemo(
    () => ({
      userCountry: scenario.userCountry,
      currentLocale: scenario.currentLocale,
      visitorSite: scenario.visitorSite,
      isMismatch: scenario.isMismatch,
      isCrossSite: scenario.isCrossSite,
      isUnsupportedCountry: scenario.isUnsupportedCountry,
      suggestedLocale: scenario.suggestedLocale,
      shouldShowModal: scenario.shouldShowModal,
      crossSiteHomeUrl: process.env.NEXT_PUBLIC_CROSS_SITE_HOME_URL || null
    }),
    [scenario]
  )

  return (
    <GeoContext.Provider value={contextValue}>{children}</GeoContext.Provider>
  )
}

export const useGeo = () => useContext(GeoContext)
